import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as TrashIcon } from '../../../../assets/icons/TrashBin2.svg';
import './VoiceNotes.scss';

export interface VoiceNote {
  id: string;
  audioBlob?: Blob;
  duration: number;
  audioUrl: string;
}

interface VoiceNotesProps {
  voiceNotes: VoiceNote[];
  onAddVoiceNote: (voiceNote: VoiceNote) => void;
  onDeleteVoiceNote: (id: string) => void;
  isReadOnly?: boolean;
}

const VoiceNotes: React.FC<VoiceNotesProps> = ({
  voiceNotes,
  onAddVoiceNote,
  onDeleteVoiceNote,
  isReadOnly = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<{ [key: string]: number }>({});

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
  const recordingTimeRef = useRef<number>(0);
  const voiceNotesRef = useRef(voiceNotes);
  voiceNotesRef.current = voiceNotes;

  useEffect(() => {
    const audioElements = audioRefs.current;
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      Object.values(audioElements).forEach(audio => {
        // Clean up blob URLs created from fetched audio
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  useEffect(() => {
    return () => {
      voiceNotesRef.current.forEach(note => {
        if (note.audioUrl && note.audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(note.audioUrl);
        }
      });
    };
  }, []);

  useEffect(() => {
    const currentIds = new Set(voiceNotes.map(note => note.id));
    
    Object.keys(audioRefs.current).forEach(id => {
      if (!currentIds.has(id)) {
        const audio = audioRefs.current[id];
        if (audio) {
          audio.pause();
          audio.src = '';
          delete audioRefs.current[id];
        }
      }
    });
    
    if (playingId && !currentIds.has(playingId)) {
      setPlayingId(null);
    }
  }, [voiceNotes, playingId]);

  const startRecording = async () => {
    try {
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        mediaRecorderRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      if (!stream || stream.getAudioTracks().length === 0) {
        throw new Error('No audio track available');
      }

      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack.enabled) {
        audioTrack.enabled = true;
      }

      streamRef.current = stream;
      audioChunksRef.current = [];

      if (typeof MediaRecorder === 'undefined') {
        alert('MediaRecorder is not supported in this browser.');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      let options: MediaRecorderOptions = {};
      const mimeTypes = [
        'audio/mp4',
        'audio/m4a',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/wav'
      ];

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          options = { mimeType };
          selectedMimeType = mimeType;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, Object.keys(options).length > 0 ? options : undefined);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event.error);
        setIsRecording(false);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        alert('An error occurred while recording. Please try again.');
      };


      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length === 0) {
          console.warn('No audio data recorded');
          setIsRecording(false);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          return;
        }

        const blobType = selectedMimeType || mediaRecorder.mimeType || 'audio/mp4';
        const audioBlob = new Blob(audioChunksRef.current, { type: blobType });
        const capturedRecordingTime = recordingTimeRef.current;
        
        try {
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          
          const handleLoadedMetadata = () => {
            let duration = Math.round(audio.duration);
            
            if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
              console.warn('Invalid audio duration, using recording time as fallback');
              duration = capturedRecordingTime > 0 ? capturedRecordingTime : 0;
            }
            
            if (duration > 0) {
              const voiceNote: VoiceNote = {
                id: Date.now().toString(),
                audioBlob: audioBlob,
                duration,
                audioUrl
              };
              onAddVoiceNote(voiceNote);
            } else {
              console.error('Invalid audio duration and no recording time available');
            }
            
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
          };

          audio.addEventListener('loadedmetadata', handleLoadedMetadata);

          audio.addEventListener('error', (e) => {
            console.error('Error loading audio metadata:', e);
            const fallbackDuration = capturedRecordingTime > 0 ? capturedRecordingTime : 0;
            if (fallbackDuration > 0) {
              const voiceNote: VoiceNote = {
                id: Date.now().toString(),
                audioBlob: audioBlob,
                duration: fallbackDuration,
                audioUrl
              };
              onAddVoiceNote(voiceNote);
            }
          });
        } catch (error) {
          console.error('Error processing audio blob:', error);
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          
          const handleLoadedMetadata = () => {
            let duration = Math.round(audio.duration);
            
            if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
              console.warn('Invalid audio duration, using recording time as fallback');
              duration = capturedRecordingTime > 0 ? capturedRecordingTime : 0;
            }
            
            if (duration > 0) {
              const voiceNote: VoiceNote = {
                id: Date.now().toString(),
                audioBlob: audioBlob,
                duration,
                audioUrl
              };
              onAddVoiceNote(voiceNote);
            }
            
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
          };

          audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        }

        setTimeout(() => {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }, 100);
      };

      try {
        mediaRecorder.start(250);
        setIsRecording(true);
        setRecordingTime(0);
        recordingTimeRef.current = 0;

        timerIntervalRef.current = setInterval(() => {
          recordingTimeRef.current += 1;
          setRecordingTime(recordingTimeRef.current);
        }, 1000);
      } catch (startError) {
        console.error('Error starting MediaRecorder:', startError);
        stream.getTracks().forEach(track => track.stop());
        alert('Failed to start recording. Please try again.');
      }
    } catch (error: any) {
      console.error('Error accessing microphone:', error);
      setIsRecording(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      alert(`Unable to access microphone: ${error.message || 'Please check your permissions.'}`);
    }
  };

  const stopRecording = () => {
    if (!isRecording) {
      return;
    }

    if (mediaRecorderRef.current) {
      try {
        const state = mediaRecorderRef.current.state;
        if (state === 'recording') {
          mediaRecorderRef.current.stop();
        } else if (state === 'paused') {
          mediaRecorderRef.current.stop();
        } else {
          console.warn('MediaRecorder is not in recording state:', state);
        }
      } catch (error) {
        console.error('Error stopping MediaRecorder:', error);
      }
    }

    setIsRecording(false);
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setRecordingTime(0);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async (voiceNote: VoiceNote) => {
    // Validate audio URL before proceeding
    if (!voiceNote.audioUrl || voiceNote.audioUrl.trim() === '') {
      console.error('Invalid audio URL: audioUrl is empty or undefined');
      alert('Audio file URL is missing. Cannot play audio.');
      return;
    }

    let audio = audioRefs.current[voiceNote.id];
    
    if (!audio) {
      try {
        // For blob URLs, use directly. For remote URLs, fetch and convert to blob
        let audioSrc = voiceNote.audioUrl;
        
        if (!voiceNote.audioUrl.startsWith('blob:')) {
          // Fetch the audio file and convert to blob URL for better compatibility
          try {
            console.log('Fetching audio from URL:', voiceNote.audioUrl);
            const response = await fetch(voiceNote.audioUrl, {
              method: 'GET',
              headers: {
                'Accept': 'audio/*',
              },
              mode: 'cors',
            });
            
            if (!response.ok) {
              throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
            }
            
            const blob = await response.blob();
            audioSrc = URL.createObjectURL(blob);
            console.log('Created blob URL from fetched audio:', audioSrc);
          } catch (fetchError) {
            console.warn('Failed to fetch audio, trying direct URL:', fetchError);
            // Fall back to direct URL if fetch fails
            audioSrc = voiceNote.audioUrl;
          }
        }
        
        // Create audio element with the URL
        audio = new Audio(audioSrc);
        audioRefs.current[voiceNote.id] = audio;
        
        // Add preload to help with loading
        audio.preload = 'auto';
        
        console.log('Loading audio:', {
          id: voiceNote.id,
          originalUrl: voiceNote.audioUrl,
          audioSrc: audioSrc,
          urlLength: voiceNote.audioUrl.length
        });
        
        audio.addEventListener('timeupdate', () => {
          setCurrentTime(prev => ({
            ...prev,
            [voiceNote.id]: Math.round(audio.currentTime)
          }));
        });

        audio.addEventListener('ended', () => {
          setPlayingId(null);
          setCurrentTime(prev => ({
            ...prev,
            [voiceNote.id]: 0
          }));
        });

        audio.addEventListener('loadedmetadata', () => {
          console.log('Audio metadata loaded:', {
            id: voiceNote.id,
            duration: audio.duration,
            readyState: audio.readyState
          });
        });

        audio.addEventListener('canplay', () => {
          console.log('Audio can play:', {
            id: voiceNote.id,
            readyState: audio.readyState
          });
        });

        audio.addEventListener('error', (e) => {
          const error = audio.error;
          let errorMessage = 'Error loading audio';
          
          if (error) {
            switch (error.code) {
              case error.MEDIA_ERR_ABORTED:
                errorMessage = 'Audio loading was aborted';
                break;
              case error.MEDIA_ERR_NETWORK:
                errorMessage = 'Network error while loading audio';
                break;
              case error.MEDIA_ERR_DECODE:
                errorMessage = 'Audio decoding error';
                break;
              case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorMessage = 'Audio format not supported or source not found';
                break;
              default:
                errorMessage = `Audio error: ${error.message || 'Unknown error'}`;
            }
          }
          
          console.error('Error loading audio:', errorMessage, e, {
            audioUrl: voiceNote.audioUrl,
            audioSrc: audio.src,
            errorCode: error?.code,
            errorMessage: error?.message,
            readyState: audio.readyState,
            networkState: audio.networkState
          });
          
          // Clean up blob URL if we created one
          if (audio.src.startsWith('blob:') && audio.src !== voiceNote.audioUrl) {
            URL.revokeObjectURL(audio.src);
          }
          
          delete audioRefs.current[voiceNote.id];
          setPlayingId(null);
        });

        // Load the audio
        audio.load();
      } catch (error) {
        console.error('Error creating audio element:', error);
        alert('Failed to initialize audio player. Please check the audio file URL.');
        return;
      }
    }

    if (playingId === voiceNote.id) {
      audio.pause();
      setPlayingId(null);
    } else {
      if (playingId) {
        const otherAudio = audioRefs.current[playingId];
        if (otherAudio) {
          otherAudio.pause();
          otherAudio.currentTime = 0;
        }
      }
      
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setPlayingId(voiceNote.id);
          })
          .catch((error) => {
            console.error('Error playing audio:', error);
            audio.load();
            setPlayingId(null);
          });
      } else {
        setPlayingId(voiceNote.id);
      }
    }
  };

  const handleDelete = (id: string) => {
    if (playingId === id) {
      const audio = audioRefs.current[id];
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setPlayingId(null);
    }
    
    const audio = audioRefs.current[id];
    if (audio) {
      // Clean up blob URL if we created one from fetched audio
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
      audio.pause();
      audio.src = '';
      delete audioRefs.current[id];
    }
    
    onDeleteVoiceNote(id);
  };

  return (
    <div 
      className="voice-notes-container"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="voice-notes-title">Voice Notes</div>
      
      {voiceNotes.length > 0 && (
        <div className="voice-notes-list">
          {voiceNotes.map((voiceNote) => {
            const isPlaying = playingId === voiceNote.id;
            const current = currentTime[voiceNote.id] || 0;
            const audio = audioRefs.current[voiceNote.id];
            const duration = voiceNote.duration > 0 
              ? voiceNote.duration 
              : (audio && audio.duration > 0 ? audio.duration : 0);
            const progress = duration > 0 ? (current / duration) * 100 : 0;

            return (
              <div key={voiceNote.id} className="voice-note-item">
                <button
                  type="button"
                  className="play-pause-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePlayPause(voiceNote);
                  }}
                >
                  {isPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="2" width="2" height="12" fill="currentColor"/>
                      <rect x="9" y="2" width="2" height="12" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 2L13 8L3 14V2Z" fill="currentColor"/>
                    </svg>
                  )}
                </button>

                {isPlaying ? (
                  <div className="voice-note-playing">
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="time-display">
                      {formatTime(current)} / {formatTime(duration)}
                    </div>
                  </div>
                ) : (
                  <div className="time-display">
                    {formatTime(duration)}
                  </div>
                )}

                {!isReadOnly && (
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(voiceNote.id);
                    }}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isReadOnly && (
        <>
          {isRecording ? (
            <div className="recording-container">
              <div className="recording-indicator">
                <div className="recording-dot"></div>
                <span className="recording-time">{formatTime(recordingTime)}</span>
              </div>
              <button 
                type="button"
                className="stop-recording-btn" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  stopRecording();
                }}
              >
                Stop
              </button>
            </div>
          ) : (
            <button 
              type="button"
              className="record-voice-btn" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                startRecording();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12C11.6569 12 13 10.6569 13 9V4C13 2.34315 11.6569 1 10 1C8.34315 1 7 2.34315 7 4V9C7 10.6569 8.34315 12 10 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 12V15M10 15C8.89543 15 8 15.8954 8 17H12C12 15.8954 11.1046 15 10 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Record Voice</span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default VoiceNotes;

